import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Zap } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";

const AdminMembers_v2 = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      const response = await axios.get(`${API}/admin/members`, {
        withCredentials: true
      });
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  const tierColors = {
    essential: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    standard: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    plus: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
  };

  const statusColors = {
    active: "bg-green-500/20 text-green-400 border-green-500/30",
    inactive: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    canceled: "bg-red-500/20 text-red-400 border-red-500/30"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-6">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-heading font-bold text-white">DueDay</span>
          </div>
          <span className="px-4 py-2 bg-yellow-400/20 border border-yellow-400/50 rounded-full text-yellow-400 text-sm font-semibold">
            Admin Panel
          </span>
        </div>
      </nav>

      <div className="container mx-auto max-w-7xl pt-16">
        <Button 
          data-testid="back-to-admin-btn"
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-6 rounded-full text-white hover:text-yellow-400 hover:bg-white/10"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Admin
        </Button>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 md:p-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold mb-2 text-white" data-testid="members-title">
                Members
              </h1>
              <p className="text-slate-400">{members.length} total members</p>
            </div>
          </div>

          {members.length === 0 ? (
            <div className="text-center py-12">
              <Shield className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No members yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="members-table">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-4 px-4 font-semibold text-slate-300">Name</th>
                    <th className="text-left py-4 px-4 font-semibold text-slate-300">Email</th>
                    <th className="text-left py-4 px-4 font-semibold text-slate-300">Location</th>
                    <th className="text-left py-4 px-4 font-semibold text-slate-300">Tier</th>
                    <th className="text-left py-4 px-4 font-semibold text-slate-300">Status</th>
                    <th className="text-right py-4 px-4 font-semibold text-slate-300">Total Advanced</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => (
                    <tr 
                      key={member.user_id}
                      data-testid={`member-row-${member.user_id}`}
                      className="border-b border-slate-700/50 hover:bg-slate-900/50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="font-medium text-white">{member.name}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-slate-400">{member.email}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-sm text-slate-300">
                          {member.city && member.state ? `${member.city}, ${member.state}` : '-'}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        {member.membership_tier ? (
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${tierColors[member.membership_tier] || 'bg-slate-700 text-slate-300'}`}>
                            {member.membership_tier}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-500">None</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${statusColors[member.membership_status] || 'bg-slate-700 text-slate-300'}`}>
                          {member.membership_status || 'inactive'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="font-semibold text-yellow-400">${member.total_advanced?.toFixed(2) || '0.00'}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMembers_v2;
